import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CountryEnum } from '@ivario-arc/shared/src/enums';
import { useMemo } from 'react';
import { ButtonType, SampleDto } from '@src/types/types';
import { Button } from '@components/atoms/Button';
import { LoadingSpinner } from '@components/atoms/LoadingSpinner';
import { IconType } from '@src/types/IconType';
import Input from '@components/atoms/Input';
import TextAreaInput from '@components/atoms/TextAreaInput';
import { Datepicker } from '@components/atoms/Datepicker';
import { Timepicker } from '@components/atoms/Timepicker';
import { Paths } from '@src/routes/paths';
import { apiClient } from '@src/api/client';

export interface CommentsFormData {
  collectionPoint?: string;
  companyName?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  collectionDate?: string;
  collectionTime?: string;
  sampler?: string;
  notes?: string;
  country?: CountryEnum;
}

export const CommentsForm = ({
  sample,
  previousPath,
}: {
  sample: SampleDto;
  previousPath?: string;
}) => {
  const { t } = useTranslation('view', { keyPrefix: 'commentsForm' });
  const navigate = useNavigate();

  const { mutateAsync: updateComments } = apiClient.useMutation(
    'post',
    `/api/sampleComment/{id}`,
    {
      onSuccess: () => {
        if (!previousPath) {
          navigate(`${Paths.PROFILE}?sampleClaimed=true`);
        } else {
          navigate(previousPath);
        }
      },
    },
  );

  const { data, isLoading, error } = apiClient.useQuery(
    'get',
    '/api/sampleComment/{id}',
    {
      params: {
        path: { id: sample.id.toString() ?? '' },
        query: { prefill: !previousPath },
      },
    },
  );

  const defaultValues: CommentsFormData = useMemo(() => {
    return {
      collectionPoint: data?.collectionPoint ?? '',
      companyName: data?.companyName ?? '',
      street: data?.street ?? '',
      houseNumber: data?.houseNumber ?? '',
      postalCode: data?.postalCode ?? '',
      city: data?.city ?? '',
      collectionDate: data?.collectionDate ?? '',
      collectionTime: data?.collectionTime ?? '',
      sampler: data?.sampler ?? '',
      notes: data?.notes ?? '',
      country: (data?.country as CountryEnum) ?? CountryEnum.Germany,
    };
  }, [data]);

  const form = useForm({
    defaultValues: defaultValues,
    onSubmit: async ({ value }) => {
      try {
        await updateComments({
          body: { ...value },
          params: { path: { id: sample.id.toString() } },
        });
      } catch {
        // error is captured in mutation state
      }
    },
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    throw error;
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        {previousPath && (
          <Button
            buttonType={ButtonType.TERTIARY}
            IconLeading={IconType.ArrowLeft}
            onClick={() => navigate(previousPath)}
            label={t('back')}
            className="w-fit px-5"
          />
        )}
      </div>
      <h1>{t('title')}</h1>
      <h2>{t('description')}</h2>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
        className="space-y-4"
      >
        <form.Field
          name="collectionPoint"
          children={(field) => (
            <Input
              label={t('samplingPoint')}
              value={field.state.value ?? ''}
              handleChange={(e) => field.handleChange(e.target.value)}
              handleBlur={field.handleBlur}
              type="text"
              name={field.name}
              id={field.name}
              errors={field.state.meta.errors}
              touched={field.state.meta.isTouched}
              testId={`comments-form-input-${field.name}`}
            />
          )}
        />

        <form.Field
          name="companyName"
          children={(field) => (
            <Input
              label={t('companyName')}
              value={field.state.value ?? ''}
              handleChange={(e) => field.handleChange(e.target.value)}
              handleBlur={field.handleBlur}
              type="text"
              name={field.name}
              id={field.name}
              errors={field.state.meta.errors}
              touched={field.state.meta.isTouched}
              testId={`comments-form-input-${field.name}`}
            />
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <form.Field
            name="street"
            children={(field) => (
              <Input
                label={t('street')}
                value={field.state.value ?? ''}
                handleChange={(e) => field.handleChange(e.target.value)}
                handleBlur={field.handleBlur}
                type="text"
                name={field.name}
                id={field.name}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
                testId={`comments-form-input-${field.name}`}
              />
            )}
          />
          <form.Field
            name="houseNumber"
            children={(field) => (
              <Input
                label={t('houseNumber')}
                value={field.state.value ?? ''}
                handleChange={(e) => field.handleChange(e.target.value)}
                handleBlur={field.handleBlur}
                type="text"
                name={field.name}
                id={field.name}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
                testId={`comments-form-input-${field.name}`}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <form.Field
            name="postalCode"
            children={(field) => (
              <Input
                label={t('postalCode')}
                value={field.state.value ?? ''}
                handleChange={(e) => field.handleChange(e.target.value)}
                handleBlur={field.handleBlur}
                type="text"
                name={field.name}
                id={field.name}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
                testId={`comments-form-input-${field.name}`}
              />
            )}
          />
          <form.Field
            name="city"
            children={(field) => (
              <Input
                label={t('city')}
                value={field.state.value ?? ''}
                handleChange={(e) => field.handleChange(e.target.value)}
                handleBlur={field.handleBlur}
                type="text"
                name={field.name}
                id={field.name}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
                testId={`comments-form-input-${field.name}`}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <form.Field
            name="collectionDate"
            children={(field) => (
              <Datepicker
                label={t('samplingDate')}
                value={field.state.value}
                handleChange={(e) => field.handleChange(e.target.value)}
                handleBlur={field.handleBlur}
                name={field.name}
                id={field.name}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
                testId={`comments-form-input-${field.name}`}
              />
            )}
          />
          <form.Field
            name="collectionTime"
            children={(field) => (
              <Timepicker
                label={t('samplingTime')}
                value={field.state.value}
                handleChange={(e) => field.handleChange(e.target.value)}
                handleBlur={field.handleBlur}
                name={field.name}
                id={field.name}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
                testId={`comments-form-input-${field.name}`}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <form.Field
            name="sampler"
            children={(field) => (
              <Input
                label={t('sampler')}
                value={field.state.value ?? ''}
                handleChange={(e) => field.handleChange(e.target.value)}
                handleBlur={field.handleBlur}
                type="text"
                name={field.name}
                id={field.name}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
                testId={`comments-form-input-${field.name}`}
              />
            )}
          />
        </div>

        <form.Field
          name="notes"
          children={(field) => (
            <TextAreaInput
              label={t('notes')}
              value={field.state.value ?? ''}
              handleChange={(e) => field.handleChange(e.target.value)}
              handleBlur={field.handleBlur}
              name={field.name}
              id={field.name}
              errors={field.state.meta.errors}
              testId={`comments-form-input-${field.name}`}
            />
          )}
        />

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button
              buttonType={ButtonType.PRIMARY}
              label={t('saveButton')}
              type="submit"
              className="w-full mt-4"
              disabled={!canSubmit}
              loading={isSubmitting}
              testingId={'comments-form-submit'}
            />
          )}
        />
      </form>
    </div>
  );
};
